/** Bound the caller's wait even if a dependency ignores cancellation. */
export async function withDeadline<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  parentSignal?: AbortSignal,
): Promise<T> {
  parentSignal?.throwIfAborted()
  const controller = new AbortController()
  let onAbort!: () => void
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_, reject) => {
    onAbort = () => {
      const reason = parentSignal!.reason
      reject(reason)
      controller.abort(reason)
    }
    parentSignal?.addEventListener('abort', onAbort, {once: true})
    timer = setTimeout(() => {
      const error = new DOMException('Dependency request timed out', 'TimeoutError')
      reject(error)
      controller.abort(error)
    }, timeoutMs)
  })
  try {
    return await Promise.race([operation(controller.signal), deadline])
  } finally {
    clearTimeout(timer)
    parentSignal?.removeEventListener('abort', onAbort)
  }
}
